import React, { useEffect, useState } from "react";
import img from "../../../assets/vehicle.png";
import { useNavigate, useParams } from "react-router-dom";

const Transport = () => {
  const navigate = useNavigate();
  const [property, setProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { id } = useParams();

  // breadcrumb
  const handleBreadcrumbClick = () => {
    navigate("/Dashboard/services");
  };

  // ========================= api integration =========================
  // Fetch property details
  useEffect(() => {
    const fetchPurchasedProperties = async () => {
      const authToken = localStorage.getItem("authToken"); // Get the user's token
      if (!authToken) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(
          "https://silver-tlc-backend-delta.vercel.app/api/v1/userpurchase/transport/get",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              token: authToken,
            }),
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }

        const data = await response.json();
        const purchasedProperties =
          data.transportPurchaseData?.transportpurchase || [];

        // Find the specific property by ID
        const foundProperty = purchasedProperties.find(
          (purchase) => purchase.transportId._id === id
        );

        if (foundProperty) {
          setProperty(foundProperty.transportId); // Set the property details
        } else {
          console.error("Property not found");
        }
      } catch (error) {
        console.error("Error fetching purchased properties:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPurchasedProperties();
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="spinner-border text-primary" role="status">
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (!property) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="text-gray-500">Transport service not found.</div>
      </div>
    );
  }

  // converting date to readable data
  const pickUpDate = property.vehicleDetails?.pickUpDate
    ? new Date(property.vehicleDetails?.pickUpDate)
    : null;
  const pickUpTime = property.vehicleDetails?.pickUpTime
    ? new Date(property.vehicleDetails?.pickUpTime)
    : null;

  // Format the date as YYYY-MM-DD if pickUpDate is valid
  const formattedDate =
    pickUpDate && !isNaN(pickUpDate)
      ? pickUpDate.toISOString().split("T")[0]
      : "Invalid Date";

  // Format the time as HH:MM:SS if pickUpTime is valid
  const formattedTime =
    pickUpTime && !isNaN(pickUpTime)
      ? pickUpTime.toISOString().split("T")[1].split(".")[0]
      : "Invalid Time";

  return (
    <div className="w-4/5 lg:w-full mx-auto bg-white rounded-lg overflow-hidden">
      <div className="flex justify-start items-start flex-col p-4 border-b">
        {/* Breadcrumbs */}
        <div className="mb-4 text-sm px-4">
          <button
            onClick={handleBreadcrumbClick}
            className="text-gray-500 hover:text-gray-700"
          >
            My Services
          </button>
          <span className="mx-2 text-gray-400">&gt;</span>
          <span className="text-purple-600 font-medium">Transportation</span>
        </div>
        <h2 className="text-lg font-semibold gap-3 flex items-center">
          <i className="mr-2 text-blue-500"></i>
          <svg
            width="24"
            height="26"
            viewBox="0 0 24 26"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M9.99995 24L1.94995 24L1.94995 2L19.95 2L19.95 13"
              stroke="#004A61"
              stroke-width="2.4"
              stroke-miterlimit="10"
              stroke-linecap="square"
            />
            <path
              d="M6.00342 8L15.9564 8"
              stroke="#004A61"
              stroke-width="2.4"
              stroke-miterlimit="10"
              stroke-linecap="square"
            />
            <path
              d="M6.00342 13L15.9564 13"
              stroke="#004A61"
              stroke-width="2.4"
              stroke-miterlimit="10"
              stroke-linecap="square"
            />
            <path
              d="M6 18H10"
              stroke="#004A61"
              stroke-width="2.4"
              stroke-miterlimit="10"
              stroke-linecap="square"
            />
            <path
              d="M22 19V24H14L14 19L18 16L22 19Z"
              stroke="#004A61"
              stroke-width="2.4"
              stroke-miterlimit="10"
              stroke-linecap="square"
            />
            <path
              d="M18 22V24"
              stroke="#004A61"
              stroke-width="2.4"
              stroke-miterlimit="10"
              stroke-linecap="square"
            />
          </svg>
          Transportation
        </h2>
      </div>

      <div className="p-6">
        {/* Info Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* First Card */}
          <div className="border border-gray-200 rounded-lg p-4">
            {[
              [" Name", `${property.vehicleDetails?.name}`],
              ["Email", `${property.vehicleDetails?.email}`],
              ["Phone", `${property.vehicleDetails?.phone}`],
              [
                "Pick Up Location",
                `${property.vehicleDetails?.pickUpLocation}`,
              ],
              [
                "Drop Off Location",
                `${property.vehicleDetails?.dropOffLocation}`,
              ],
            ].map(([label1, label2], index) => (
              <div
                key={index}
                className="flex justify-between py-2 border-b last:border-0 border-gray-100"
              >
                <span>{label1}</span>
                <span>{label2}</span>
              </div>
            ))}
          </div>

          {/* Second Card */}
          <div className="border border-gray-200 rounded-lg p-4">
            {[
              ["Pick Up Date", `${formattedDate}`],
              ["Pick Up Time", `${formattedTime}`],
              ["Distance", `${property.vehicleDetails?.distance}`],
              ["Companions", `${property.vehicleDetails?.companions}`],
              ["Wait Time", `${property.vehicleDetails?.waitTime}`],
            ].map(([label1, label2], index) => (
              <div
                key={index}
                className="flex justify-between py-2 border-b last:border-0 border-gray-100"
              >
                <span>{label1}</span>
                <span>{label2}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Uploaded Files */}
        <div>
          <h3 className="text-lg font-semibold mb-4 ">Uploaded Files</h3>
          <div
            className="flex justify-center items-center gap-5
           overflow-x-auto"
          >
            {property.uploadFiles?.map((imageUrl, index) => (
              <div key={index} className="flex-1 h-[20rem] lg:h-full w-[20rem]">
                <img
                  src={imageUrl}
                  alt={`Property ${index}`}
                  className="w-full h-full object-cover rounded-md"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Transport;
